import React, { useEffect, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import {
    Card,
    Space,
    Tabs,
    Typography,
    Button,
    Spin,
    Alert,
    Table,
    Tag,
} from "antd";
import type { TabsProps } from "antd";
import { API_ENDPOINTS, QUERY_KEYS } from "../../api/apiEndpoints";
import {
    API_ENDPOINTS as API_MANAGEMENT_ENDPOINTS,
    QUERY_KEYS as API_MANAGEMENT_QUERY_KEYS,
} from "../../../management/api/apiEndpoints";
import { get, post } from "@/common/services/httpClient";
import { useAppQuery } from "@/common/hooks/useAppQuery";
import type { CustomerType } from "../../types/customersTypes";
import type { ApiResponse } from "../../../auth/types/authTypes";
import type { CompanyType } from "../../../participants/types/participantsTypes";
import type { UserType } from "../../../management/types/managementTypes";
import {
    CheckCircleOutlined,
    StopOutlined,
    SyncOutlined,
    PlusOutlined,
    EditOutlined,
} from "@ant-design/icons";
import Confirmation from "../../../../common/components/ConfirmButton";
import { getPermissionKeyFromApi } from "../../../../common/components/fromatPermission";
import { hasPermission } from "../../../../common/services/permissionService";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

// Fetch Customer Detail
const fetchCustomerDetail = async (Id: string): Promise<CustomerType> => {
    const response = await get<ApiResponse<CustomerType>>(
        `${API_ENDPOINTS.CUSTOMERS.GET_CUSTOMER_BY_DETAIL}?Id=${Id}`
    );
    return response.data;
};

const Detail: React.FC = () => {
    const { t } = useTranslation();
    const router = useRouter();
    const state = router?.state?.location?.state as { record?: CustomerType } | undefined;
    const record = state?.record;
    const Id = record?.id?.toString() || "";

    // Fetch main customer data
    const { data: CustomerData, isLoading, isError } = useAppQuery(
        [QUERY_KEYS.CUSTOMERS.CUSTOMER, Id],
        () => fetchCustomerDetail(Id),
        { enabled: !!Id }
    );

    const [users, setUsers] = useState<UserType[]>([]);
    const [companies, setCompanies] = useState<CompanyType[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [loadingCompanies, setLoadingCompanies] = useState(false);

    // --- Fetch Users ---
    const fetchUsers = async () => {
        if (!Id) return;
        setLoadingUsers(true);
        try {
            const response = await post<ApiResponse<UserType[]>>(
                API_ENDPOINTS.CUSTOMERS.GET_USERS,
                { CustomerId: Id }
            );
            setUsers(response.data || []);
        } catch (error) {
            console.error("Error fetching users:", error);
            setUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    };

    // --- Fetch Companies ---
    const fetchCompanies = async () => {
        if (!Id) return;
        setLoadingCompanies(true);
        try {
            const response = await post<ApiResponse<CompanyType[]>>(
                API_ENDPOINTS.CUSTOMERS.SEARCH_COMPANY,
                { CustomerId: Id }
            );
            setCompanies(response.data || []);
        } catch (error) {
            console.error("Error fetching companies:", error);
            setCompanies([]);
        } finally {
            setLoadingCompanies(false);
        }
    };

    useEffect(() => {
        if (Id) {
            fetchUsers();
            fetchCompanies();
        }
    }, [Id]);

    const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
        <div
            style={{
                border: "1px solid #d9d9d9",
                borderRadius: 8,
                padding: "24px",
                marginBottom: "24px",
                backgroundColor: "#fff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
        >
            <h3 style={{ marginBottom: 16 }}>{title}</h3>
            {children}
        </div>
    );

    // --- User Table Columns ---
    const userColumns = [
        { title: t("Name"), dataIndex: "name", key: "name" },
        { title: t("User Name"), dataIndex: "userName", key: "userName" },
        { title: t("Phone No"), dataIndex: "cellNo", key: "cellNo" },
        {
            title: t("Active"),
            dataIndex: "isActive",
            key: "isActive",
            render: (isActive: boolean) => (
                <Tag color={isActive ? "green" : "red"}>{isActive ? "Yes" : "No"}</Tag>
            ),
        },
        {
            title: t("Actions"),
            key: "actions",
            render: (record: UserType) => (
                <>
                    {!record.isActive && !record.isEmailActivated && (
                        <Confirmation
                            apiUrl={API_MANAGEMENT_ENDPOINTS.MANAGEMENT.RESEND_ACTIVATION_EMAIL_USER}
                            id={record.email}
                            title="Resend Email"
                            method="post"
                            icon={<SyncOutlined />}
                            payloadKey="email"
                            confirmButtonText={t(`Are you sure you want to resend activation email ?`)}
                            queryKey={API_MANAGEMENT_QUERY_KEYS.MANAGEMENT.USERS}
                            successMessage="Email sent successfully"
                        />
                    )}
                    {record.isEmailActivated && record.isActive && (
                        <Confirmation
                            apiUrl={API_MANAGEMENT_ENDPOINTS.MANAGEMENT.ACTIVATE_DEACTIVATE_USER}
                            id={record.id}
                            title="Deactivate"
                            method="post"
                            icon={<StopOutlined style={{ color: "red" }} />}
                            isPostById={true}
                            payloadKey="Id"
                            confirmButtonText="Are you sure you want to deactivate this user?"
                            queryKey={API_MANAGEMENT_QUERY_KEYS.MANAGEMENT.USERS}
                            successMessage="User deactivated successfully"
                        />
                    )}
                    {record.isEmailActivated && !record.isActive && (
                        <Confirmation
                            apiUrl={API_MANAGEMENT_ENDPOINTS.MANAGEMENT.ACTIVATE_DEACTIVATE_USER}
                            id={record.id}
                            title="Activate"
                            method="post"
                            icon={<CheckCircleOutlined style={{ color: "green" }} />}
                            isPostById={true}
                            payloadKey="Id"
                            confirmButtonText="Are you sure you want to activate this user?"
                            queryKey={API_MANAGEMENT_QUERY_KEYS.MANAGEMENT.USERS}
                            successMessage="User activated successfully"
                        />
                    )}

                    {/* ✅ Edit Button (outlined, blue, spaced) */}
                    {
                        hasPermission(getPermissionKeyFromApi(`${API_MANAGEMENT_ENDPOINTS.MANAGEMENT.GET_USER_BY_ID}`)) && (
                            <Button
                                type="default"
                                icon={<EditOutlined style={{ color: '#1890ff' }} />}
                                title={t("Edit User")}
                                style={{ marginLeft: 8, borderColor: '#1890ff' }}
                                onClick={async () => {
                                    try {
                                        const response = await get<ApiResponse<UserType>>(
                                            `${API_MANAGEMENT_ENDPOINTS.MANAGEMENT.GET_USER_BY_ID}/${record?.id}`
                                        );
                                        const userDetail = response.data;
                                        router.navigate({
                                            to: "/customer/detail/user",
                                            state: {
                                                customerId: record?.customerId,
                                                record: userDetail,
                                                customername: state?.record?.name,
                                                userId: userDetail?.id,
                                            } as Record<string, unknown>,
                                        });
                                    } catch (error) {
                                        console.error("Error fetching user detail:", error);
                                    }
                                }}
                            />
                        )}
                </>
            ),
        },
    ];

    // --- Company Table Columns ---
    const companyColumns = [
        {
            title: t("Name"),
            key: "name",
            render: (_: any, record: CompanyType) => (
                <Button
                    type="link"
                    onClick={() =>
                        router.navigate({
                            to: "/participants/company/Detail",
                            state: { record } as Record<string, unknown>,
                        })
                    }
                >
                    {record.name}
                </Button>
            ),
        },
        { title: t("Customer"), dataIndex: "customerName", key: "customerName" },
        { title: t("Phone"), dataIndex: "phoneNo", key: "phoneNo" },
        { title: t("Country"), dataIndex: "country", key: "country" },
        { title: t("City"), dataIndex: "city", key: "city" },
        {
            title: t("Status"),
            dataIndex: "status",
            key: "status",
            render: (status: number) => {
                let color = "default";
                let label = "Unknown";
                switch (status) {
                    case 1:
                        color = "green";
                        label = "Active";
                        break;
                    case 2:
                        color = "red";
                        label = "Inactive";
                        break;
                }
                return <Tag color={color}>{label}</Tag>;
            },
        },
    ];

    const onChange = (key: string) => console.log("Selected Tab:", key);

    const items: TabsProps["items"] = [
        {
            key: "1",
            label: t("User"),
            children: (
                <Section title="">
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 16,
                        }}
                    >
                        <h3 style={{ margin: 0 }}>{t("User Management")}</h3>
                        {
                            hasPermission(getPermissionKeyFromApi(`${API_MANAGEMENT_ENDPOINTS.MANAGEMENT.SAVE_USER}`)) && (

                                <Button
                                    type="primary"
                                    onClick={() =>
                                        router.navigate({
                                            to: "/customer/detail/user",
                                            state: {
                                                customerId: record?.id,
                                                customername: record?.name
                                            } as Record<string, unknown>,
                                        })
                                    }
                                >
                                    <PlusOutlined /> {t("Add User")}
                                </Button>
                            )}
                    </div>

                    {loadingUsers ? (
                        <Spin />
                    ) : (
                        <Table
                            columns={userColumns}
                            dataSource={users}
                            rowKey="id"

                            pagination={{
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} items`,
                            }}
                        />
                    )}
                </Section>
            ),
        },
        {
            key: "2",
            label: t("Company Detail"),
            children: (
                <Section title={t("Company Detail")}>
                    {loadingCompanies ? (
                        <Spin />
                    ) : (
                        <Table
                            columns={companyColumns}
                            dataSource={companies}
                            rowKey="id"

                                pagination={{
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} items`,
                            }}
                        />
                    )}
                </Section>
            ),
        },
    ];

    return (
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "24px" }}>
            <Card style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <Title level={3} style={{ margin: 0 }}>
                        <Space size="small">
                            {CustomerData?.name || "Customer Detail"}
                        </Space>
                    </Title>
                    <Space size="middle" wrap>
                        <Text>{CustomerData?.address} {CustomerData?.country}</Text>
                    </Space>
                    <Space size="middle" wrap>
                        <Text>{CustomerData?.email},</Text>
                        <Text>{CustomerData?.phoneNo}</Text>
                    </Space>
                </div>
            </Card>

            {isLoading && <Spin size="large" />}
            {isError && <Alert type="error" message="Error fetching data" />}

            {!isLoading && !isError && (
                <Tabs
                    defaultActiveKey="1"
                    items={items}
                    onChange={onChange}
                    destroyInactiveTabPane={false}
                />
            )}
        </div>
    );
};

export default Detail;